import { useEffect, useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { getShareAllowedEmailDomainsForTenant } from '../../config';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { useAuthContext } from '../../auth/AuthContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { addFormUser, fetchFormUsers, fetchShareHistory, removeFormUser } from '../../service/form';
import type { FormAccessEntry, FormUsersResponse, ShareHistoryResponse } from '../../service/types';
import { Query } from '../../types';
import { resolveSelectedFormId } from '../../utils/form-id';
import { validateField } from '../../utils/form-utils';
import ErrorMessage from '../../components/form/ErrorMessage';
import Notification from '../../components/Notification/Notification';
import { ApiError } from '../../utils/api-error';
import {
  allowedDomainValidationMessage,
  emailMatchesAllowedDomains,
} from '../../utils/email-domain-utils';

const formatLastActivity = (value?: string) => {
  if (!value) return '-';
  const normalized =
    value.includes(' ') && !value.includes('T') ? `${value.replace(' ', 'T')}Z` : value;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};

const formatShareHistoryDateTime = (value?: string) => {
  if (!value) return '-';
  const normalized =
    value.includes(' ') && !value.includes('T') ? `${value.replace(' ', 'T')}Z` : value;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const parseLastActivityTime = (value?: string): number => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const normalized =
    value.includes(' ') && !value.includes('T') ? `${value.replace(' ', 'T')}Z` : value;
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const toProperCase = (value?: string | null) => {
  if (!value) return '-';
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '-';
  return cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const normalizeRoleValue = (value?: string) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  if (key === 'read only' || key === 'read_only' || key === 'readonly') return 'read_only';
  if (key === 'editor') return 'editor';
  if (key === 'owner') return 'owner';
  if (key === 'approver') return 'approver';
  if (key === 'admin') return 'admin';
  return '';
};

const getAllowedRoles = (actorRole: string, approvalEnabled: boolean): string[] => {
  const normalized = String(actorRole || '')
    .trim()
    .toLowerCase();
  if (normalized === 'admin') {
    return ['admin'];
  }
  if (normalized === 'owner') {
    return approvalEnabled ? ['read_only', 'editor', 'approver'] : ['read_only', 'editor'];
  }
  if (normalized === 'approver') {
    return approvalEnabled ? ['read_only', 'approver'] : ['read_only'];
  }
  if (normalized === 'editor') {
    return approvalEnabled ? ['read_only', 'editor', 'approver'] : ['read_only', 'editor'];
  }
  return [];
};

const ROLE_LABELS: Record<string, string> = {
  read_only: 'Read Only',
  editor: 'Editor',
  owner: 'Owner',
  approver: 'Approver',
  admin: 'Admin',
};

const AllFormsIcon = ({ enabled }: { enabled: boolean }) => {
  if (enabled) {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" role="img" aria-label="All forms access">
        <rect x="1" y="1" width="18" height="18" rx="2" fill="#00703c" stroke="#005a30" />
        <path
          d="M5 10.5L8.3 13.7L15 7"
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 20 20" role="img" aria-label="Current form only">
      <rect x="1" y="1" width="18" height="18" rx="2" fill="#d4351c" stroke="#b02a17" />
      <path
        d="M6.2 6.2L13.8 13.8M13.8 6.2L6.2 13.8"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

type ShareFormValues = {
  email: string;
  role: string;
  scope: string;
  notes: string;
};

export default function ShareScreen() {
  const id = useId();
  const { tenant, formConfig } = useFormConfig();
  const { fetchJWT } = useAuthContext();
  const shareAllowedEmailDomains = getShareAllowedEmailDomainsForTenant(tenant);
  const location = useLocation();
  const queryClient = useQueryClient();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const hasSelectedForm = Boolean(selectedFormId);
  const { data: formData } = useFormData();
  const [shareFeedback, setShareFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isShareHistoryOpen, setIsShareHistoryOpen] = useState(false);

  const { data: usersData } = useQuery<FormUsersResponse>({
    queryKey: [Query.FormUsers, selectedFormId || ''],
    enabled: hasSelectedForm,
    queryFn: async () => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token || !selectedFormId) {
        return { access: [] };
      }
      return fetchFormUsers(token, { form_id: selectedFormId });
    },
    staleTime: 0,
    retry: false,
  });

  const access = (usersData?.access || []) as FormAccessEntry[];
  const currentUserId = usersData?.currentUserId;
  const shareHistoryQuery = useQuery<ShareHistoryResponse>({
    queryKey: [Query.ShareHistory, selectedFormId || ''],
    enabled: Boolean(selectedFormId) && isShareHistoryOpen,
    queryFn: async () => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token || !selectedFormId) {
        return { history: [] };
      }
      return fetchShareHistory(token, { form_id: selectedFormId });
    },
    staleTime: 0,
    retry: false,
  });
  const shareHistory = shareHistoryQuery.data?.history || [];

  const {
    formState: { isValid },
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
  } = useForm<ShareFormValues>({
    defaultValues: { email: '', role: '', scope: '', notes: '' },
    mode: 'onChange',
  });
  const emailValue = watch('email');
  const roleValue = watch('role');
  const scopeValue = watch('scope');
  const showNotes =
    Boolean(emailValue?.trim()) && Boolean(roleValue?.trim()) && Boolean(scopeValue?.trim());

  const resolveMutationErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) {
      return error.detail || error.message || 'Error: failed to add user, try again later';
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return 'Error: failed to add user, try again later';
  };

  const { mutateAsync: addUser } = useMutation({
    mutationFn: async (values: ShareFormValues) => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token) {
        throw new Error('Unable to share form');
      }
      const params: Record<string, string> = selectedFormId ? { form_id: selectedFormId } : {};
      return addFormUser(
        token,
        {
          email: values.email,
          role: values.role,
          scope: values.scope,
          notes: values.notes,
        },
        params,
      );
    },
    onSuccess: async () => {
      setShareFeedback({ type: 'success', message: 'User sucessfully added' });
      reset({ email: '', role: '', scope: '', notes: '' });
      await queryClient.invalidateQueries({ queryKey: [Query.FormUsers, selectedFormId || ''] });
      await queryClient.invalidateQueries({ queryKey: [Query.ShareHistory, selectedFormId || ''] });
      await queryClient.refetchQueries({ queryKey: [Query.FormUsers, selectedFormId || ''] });
    },
    onError: (error) => {
      setShareFeedback({
        type: 'error',
        message: resolveMutationErrorMessage(error),
      });
    },
  });
  const { mutateAsync: removeUser } = useMutation({
    mutationFn: async (targetUserId: string) => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token || !selectedFormId) {
        throw new Error('Unable to remove shared user');
      }
      return removeFormUser(
        token,
        {
          userId: targetUserId,
        },
        { form_id: selectedFormId },
      );
    },
    onSuccess: async () => {
      setShareFeedback({ type: 'success', message: 'User successfully removed' });
      await queryClient.invalidateQueries({ queryKey: [Query.FormUsers, selectedFormId || ''] });
      await queryClient.invalidateQueries({ queryKey: [Query.ShareHistory, selectedFormId || ''] });
      await queryClient.refetchQueries({ queryKey: [Query.FormUsers, selectedFormId || ''] });
    },
    onError: () => {
      setShareFeedback({
        type: 'error',
        message: 'Error: failed to remove user, try again later',
      });
    },
  });

  const sortedAccess = useMemo(() => {
    return [...access].sort((a, b) => {
      if (a.userId && a.userId === currentUserId) return -1;
      if (b.userId && b.userId === currentUserId) return 1;
      const byLastActive =
        parseLastActivityTime(b.lastActive) - parseLastActivityTime(a.lastActive);
      if (byLastActive !== 0) {
        return byLastActive;
      }
      const aEmail = String(a.email || '')
        .trim()
        .toLowerCase();
      const bEmail = String(b.email || '')
        .trim()
        .toLowerCase();
      return aEmail.localeCompare(bEmail, 'en');
    });
  }, [access, currentUserId]);
  const ownerCount = access.filter(
    (entry) => String(entry.role || '').toLowerCase() === 'owner',
  ).length;
  const currentRole = useMemo(() => {
    const currentRecord = access.find(
      (entry) => String(entry.userId || '') === String(currentUserId || ''),
    );
    const roleFromCurrentForm = String(currentRecord?.role || '')
      .trim()
      .toLowerCase();
    if (roleFromCurrentForm) return roleFromCurrentForm;

    if (
      String(formData?.role || '')
        .trim()
        .toLowerCase() === 'owner' ||
      (Array.isArray(formData?.sessionRoles) &&
        formData.sessionRoles.some(
          (role) => String(role || '').trim().toLowerCase() === 'owner',
        )) ||
      (Array.isArray(formData?.forms) &&
        formData.forms.some((form) => String(form?.role || '').trim().toLowerCase() === 'owner'))
    ) {
      return 'owner';
    }

    return String(formData?.role || '')
      .trim()
      .toLowerCase();
  }, [access, currentUserId, formData?.forms, formData?.role, formData?.sessionRoles]);
  const isOwner = currentRole === 'owner';
  const approvalEnabled =
    typeof formConfig?.approval === 'string' && formConfig.approval.trim().length > 0;
  const allowedRoleValues = useMemo(
    () => getAllowedRoles(currentRole, approvalEnabled),
    [currentRole, approvalEnabled],
  );
  const allowedRoleOptions = useMemo(
    () =>
      allowedRoleValues.map((roleValue) => ({
        value: ROLE_LABELS[roleValue],
        label: ROLE_LABELS[roleValue],
      })),
    [allowedRoleValues],
  );

  useEffect(() => {
    if (!roleValue?.trim()) return;
    if (!allowedRoleValues.includes(normalizeRoleValue(roleValue))) {
      setValue('role', '');
    }
  }, [allowedRoleValues, roleValue, setValue]);
  useEffect(() => {
    if (!shareFeedback) return;
    if (emailValue?.trim() || roleValue?.trim()) {
      setShareFeedback(null);
    }
  }, [emailValue, roleValue, shareFeedback]);

  const serviceName = formConfig?.formName || tenant || 'Share';
  const scopeOptions = useMemo(
    () =>
      !hasSelectedForm
        ? [
            {
              value: 'all_forms',
              label: `All forms in my ${String(tenant || '').toUpperCase()} account, including future forms`,
            },
          ]
        : normalizeRoleValue(roleValue) === 'admin'
        ? [{ value: 'this_form', label: 'Create a new unique form for this admin user' }]
        : [
            { value: 'this_form', label: 'Current form only' },
            {
              value: 'all_forms',
              label: `All forms in my ${String(tenant || '').toUpperCase()} account, including future forms`,
            },
          ],
    [hasSelectedForm, roleValue, tenant],
  );

  useEffect(() => {
    if (!hasSelectedForm) {
      if (scopeValue !== 'all_forms') {
        setValue('scope', 'all_forms', { shouldDirty: true, shouldValidate: true });
      }
      return;
    }
    if (normalizeRoleValue(roleValue) !== 'admin') return;
    if (scopeValue === 'this_form') return;
    setValue('scope', 'this_form', { shouldDirty: true, shouldValidate: true });
  }, [hasSelectedForm, roleValue, scopeValue, setValue]);

  return (
    <PageTemplate title={serviceName}>
      <div className="govuk-body">
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-full">
            {shareFeedback && (
              <Notification
                header={shareFeedback.type === 'success' ? 'Success' : 'Error'}
                content={shareFeedback.message}
                className={
                  shareFeedback.type === 'success'
                    ? 'govuk-notification-banner--success'
                    : 'govuk-notification-banner--error'
                }
              />
            )}
            {!hasSelectedForm && (
              <div className="govuk-inset-text">
                To share a single form, open that form and then access the Share screen.
              </div>
            )}
            <div className="share-history-actions">
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={() => setIsShareHistoryOpen(true)}
                disabled={!hasSelectedForm}
              >
                Share History
              </button>
            </div>
            <form
              className="govuk-form-group govuk-!-margin-bottom-4 share-form-row"
              onSubmit={handleSubmit(async (values) => {
                await addUser(values);
              })}
              noValidate
            >
              <div className="share-add-user-box">
                <div className="share-add-user-grid">
                  <div className="share-form-inline">
                    <div className="share-form-field share-form-field--email">
                      <label className="govuk-label" htmlFor={`${id}-share-email`}>
                        Email
                      </label>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          validate: (value: string) => {
                            const baseValidation = validateField(
                              'Email',
                              { required: true, email: true },
                              value,
                            );
                            if (baseValidation) return baseValidation;
                            if (
                              shareAllowedEmailDomains.length > 0 &&
                              !emailMatchesAllowedDomains(value, shareAllowedEmailDomains)
                            ) {
                              return allowedDomainValidationMessage(shareAllowedEmailDomains);
                            }
                            return undefined;
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <input
                              {...field}
                              id={`${id}-share-email`}
                              type="email"
                              placeholder="name@domain.gov.uk"
                              className="govuk-input"
                            />
                            <div className="share-form-error-slot">
                              {fieldState.error && (fieldState.isTouched || fieldState.isDirty) ? (
                                <ErrorMessage error={fieldState.error} />
                              ) : null}
                            </div>
                          </>
                        )}
                      />
                    </div>
                    <div className="share-form-field share-form-field--role">
                      <label className="govuk-label" htmlFor="share-role">
                        Role
                      </label>
                      <Controller
                        name="role"
                        control={control}
                        rules={{
                          validate: (value: string) =>
                            validateField('Role', { required: true }, value),
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <select
                              className="govuk-select"
                              id="share-role"
                              {...field}
                              value={field.value ?? ''}
                              disabled={allowedRoleOptions.length === 0}
                            >
                              <option value="">Select role</option>
                              {allowedRoleOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="share-form-error-slot">
                              {fieldState.error && (fieldState.isTouched || fieldState.isDirty) ? (
                                <ErrorMessage error={fieldState.error} />
                              ) : null}
                            </div>
                          </>
                        )}
                      />
                    </div>
                  </div>
                  <div className="share-form-field share-form-field--scope">
                    <label className="govuk-label" htmlFor="share-scope">
                      Scope
                    </label>
                    <Controller
                      name="scope"
                      control={control}
                      rules={{
                        validate: (value: string) =>
                          validateField('Scope', { required: true }, value),
                      }}
                      render={({ field, fieldState }) => (
                        <>
                          <select
                            className="govuk-select"
                            id="share-scope"
                            {...field}
                            value={field.value ?? ''}
                            disabled={!hasSelectedForm || normalizeRoleValue(roleValue) === 'admin'}
                          >
                            <option value="">Please select scope...</option>
                            {scopeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="share-form-error-slot">
                            {fieldState.error && (fieldState.isTouched || fieldState.isDirty) ? (
                              <ErrorMessage error={fieldState.error} />
                            ) : null}
                          </div>
                        </>
                      )}
                    />
                  </div>
                  <div className="share-form-actions">
                    <button
                      type="submit"
                      className="govuk-button govuk-button--green"
                      data-module="govuk-button"
                      data-prevent-double-click="true"
                      disabled={
                        !isValid ||
                        (!hasSelectedForm && scopeValue !== 'all_forms') ||
                        allowedRoleOptions.length === 0
                      }
                    >
                      <span>
                        Add
                        <br />
                        user
                      </span>
                    </button>
                  </div>
                </div>
                {showNotes && (
                  <div className="govuk-form-group govuk-!-margin-top-4">
                    <label className="govuk-label" htmlFor={`${id}-share-notes`}>
                      Notes to invitee
                    </label>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          id={`${id}-share-notes`}
                          className="govuk-textarea"
                          rows={4}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            </form>

            {hasSelectedForm && (
              <table className="govuk-table">
              <thead className="govuk-table__head">
                <tr className="govuk-table__row">
                  <th scope="col" className="govuk-table__header">
                    Email
                  </th>
                  <th scope="col" className="govuk-table__header share-last-active-col">
                    Last Active
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Role
                  </th>
                  <th scope="col" className="govuk-table__header share-all-forms-col">
                    All forms
                  </th>
                  <th scope="col" className="govuk-table__header">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="govuk-table__body">
                {sortedAccess.map((entry) => (
                  <tr className="govuk-table__row" key={`${entry.userId}-${entry.email}`}>
                    {(() => {
                      const roleKey = String(entry.role || '')
                        .trim()
                        .toLowerCase();
                      const showsAllForms = roleKey === 'owner' || Boolean(entry.allForms);
                      return (
                        <>
                          <td className="govuk-table__cell">{entry.email || '-'}</td>
                          <td className="govuk-table__cell share-last-active-col">
                            {formatLastActivity(entry.lastActive)}
                          </td>
                          <td className="govuk-table__cell">{toProperCase(entry.role)}</td>
                          <td className="govuk-table__cell share-all-forms-col">
                            <AllFormsIcon enabled={showsAllForms} />
                          </td>
                          <td className="govuk-table__cell">
                            {!isOwner || entry.userId === currentUserId ? null : String(
                                entry.role || '',
                              ).toLowerCase() === 'owner' && ownerCount <= 1 ? (
                              <button
                                type="button"
                                className="govuk-button govuk-button--red"
                                data-module="govuk-button"
                                data-prevent-double-click="true"
                                disabled
                              >
                                Remove
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="govuk-button govuk-button--red"
                                data-module="govuk-button"
                                data-prevent-double-click="true"
                                onClick={async () => {
                                  if (!entry.userId) return;
                                  await removeUser(entry.userId);
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
              </table>
            )}

            {isShareHistoryOpen && (
              <div className="share-history-backdrop">
                <div
                  className="share-history-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="share-history-title"
                >
                  <div className="share-history-header">
                    <div>
                      <h2
                        id="share-history-title"
                        className="govuk-heading-m govuk-!-margin-bottom-2"
                      >
                        Share History
                      </h2>
                      <p className="govuk-body govuk-!-margin-bottom-0">
                        {shareHistoryQuery.isLoading
                          ? 'Loading share history...'
                          : `${shareHistory.length} share history entr${shareHistory.length === 1 ? 'y' : 'ies'}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                      data-module="govuk-button"
                      onClick={() => setIsShareHistoryOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                  {shareHistoryQuery.isError && (
                    <Notification
                      header="Error"
                      content="Unable to load share history right now."
                      className="govuk-notification-banner--error"
                    />
                  )}
                  {!shareHistoryQuery.isLoading && !shareHistoryQuery.isError && (
                    <div className="share-history-table-wrap">
                      <table className="govuk-table">
                        <thead className="govuk-table__head">
                          <tr className="govuk-table__row">
                            <th scope="col" className="govuk-table__header">
                              Datetime
                            </th>
                            <th scope="col" className="govuk-table__header">
                              Form Shared
                            </th>
                            <th scope="col" className="govuk-table__header">
                              Shared With
                            </th>
                            <th scope="col" className="govuk-table__header">
                              Shared By
                            </th>
                          </tr>
                        </thead>
                        <tbody className="govuk-table__body">
                          {shareHistory.length === 0 ? (
                            <tr className="govuk-table__row">
                              <td className="govuk-table__cell" colSpan={4}>
                                No share history found.
                              </td>
                            </tr>
                          ) : (
                            shareHistory.map((entry, index) => {
                              const action = String(entry.action || '')
                                .trim()
                                .toLowerCase();
                              const actionLabel = action === 'remove' ? 'Removed' : 'Shared';
                              const isAllForms = entry.formShared === 'All Forms';
                              const formLabel = isAllForms
                                ? 'All Forms'
                                : entry.formId || entry.formShared || '-';
                              const identifier = entry.formIdentifier || formLabel;

                              return (
                                <tr
                                  className="govuk-table__row"
                                  key={`${entry.datetime}-${entry.sharedWith}-${index}`}
                                >
                                  <td className="govuk-table__cell">
                                    {formatShareHistoryDateTime(entry.datetime)}
                                  </td>
                                  <td className="govuk-table__cell">
                                    {isAllForms ? (
                                      <span>{formLabel}</span>
                                    ) : (
                                      <details className="share-history-form-details">
                                        <summary
                                          className="share-history-form-box"
                                          title={identifier}
                                        >
                                          {formLabel}
                                        </summary>
                                        <span className="share-history-form-identifier">
                                          Identifier: {identifier}
                                        </span>
                                      </details>
                                    )}
                                    <span
                                      className={`share-history-action-tag share-history-action-tag--${action || 'share'}`}
                                    >
                                      {actionLabel}
                                    </span>
                                  </td>
                                  <td className="govuk-table__cell">{entry.sharedWith || '-'}</td>
                                  <td className="govuk-table__cell">{entry.sharedBy || '-'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Link className="govuk-link" to={`/${tenant}`}>
              Return to webform
            </Link>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
}
